import { Resource, AssetAttributes, isSynced } from '../api/types';
import { isMarketPriced, isStaleValuation, daysSince } from './valuation';
import { compareByString } from '../utils/sort';

/**
 * Minimal shape the document-expiry loop needs. A full `Resource<DocumentAttributes>`
 * satisfies this structurally (its `title`/`expiresAt` are a subset), and so does the
 * Dashboard summary endpoint's lighter `DashboardAttentionDocument` once wrapped in an
 * `attributes` envelope — letting the Dashboard hand over just the near-expiry documents
 * the façade already filtered, instead of every document, with no change here.
 */
export interface AttentionDocumentSource {
  id: string;
  attributes: { title?: string; expiresAt?: string };
}

/**
 * Things the app noticed on the user's behalf — surfaced on the Dashboard so
 * nobody has to go hunting for what's out of date. Pure function over already-
 * fetched resources; no extra network cost.
 */
export interface AttentionItem {
  key: string;
  label: string;
  detail: string;
  href: string;
  /** Lower sorts first. */
  severity: number;
}

/** Days ahead a document expiry starts to surface. */
export const EXPIRY_WINDOW_DAYS = 60;

/**
 * Severity for a broken account connection: **above everything else**, including an
 * already-expired document.
 *
 * The reasoning is about what the user can see without help. An expired document is a
 * task they can find by looking; the figure on the Dashboard is still true. A broken
 * connection is the opposite: net worth, allocation, and holdings all keep rendering
 * confidently from the last successful sync, with nothing on the page admitting the
 * numbers stopped moving. It is the only item in this list where NOT surfacing it leaves
 * the rest of the Dashboard quietly wrong.
 */
const CONNECTION_SEVERITY = -1;

/** How the summary endpoint describes a connection that stopped working. */
export interface AttentionConnectionSource {
  id: string;
  institutionName: string;
  status: 'error' | 'disconnected';
  lastSyncedAt?: string;
}

/**
 * Whole days since a full ISO **instant** (`2026-08-03T14:23:59.786Z`).
 *
 * Deliberately not `valuation.ts`'s `daysSince`, which appends `T00:00:00Z` to its input
 * and so only accepts a date-only string. `lastSyncedAt` is written by account-sync as
 * `new Date().toISOString()`, which that helper turns into `Invalid Date` -> `null`, and
 * the day count would have silently never rendered.
 */
function daysSinceInstant(iso: string): number | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
}

function daysUntil(iso: string): number | null {
  const then = new Date(`${iso}T00:00:00Z`).getTime();
  if (Number.isNaN(then)) return null;
  return Math.ceil((then - Date.now()) / (24 * 60 * 60 * 1000));
}

export function attentionItems(
  assets: Resource<AssetAttributes>[],
  documents: AttentionDocumentSource[],
  /** Optional so existing callers and tests that predate connections still compile;
   *  the Dashboard always passes them. */
  connections: AttentionConnectionSource[] = [],
): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const conn of connections) {
    const days = conn.lastSyncedAt ? daysSinceInstant(conn.lastSyncedAt) : null;
    // Says how long the numbers have been standing still, which is the part that
    // actually matters, rather than restating the status word the label already carries.
    const detail = conn.status === 'disconnected'
      ? 'Disconnected. Connect it again to keep these balances up to date.'
      : days !== null && days >= 1
        ? `Not updated in ${Math.floor(days)} day${Math.floor(days) === 1 ? '' : 's'}. Choose Manage connection to resume updates.`
        : 'Choose Manage connection to resume updates.';
    items.push({
      key: `connection-${conn.status}-${conn.id}`,
      label: conn.institutionName,
      detail,
      href: '/settings/connections',
      severity: CONNECTION_SEVERITY,
    });
  }

  for (const doc of documents) {
    const exp = doc.attributes.expiresAt;
    if (!exp) continue;
    const days = daysUntil(exp);
    if (days === null) continue;
    if (days < 0) {
      items.push({
        key: `doc-expired-${doc.id}`,
        label: doc.attributes.title ?? 'Untitled',
        detail: `Expired ${-days} day${days === -1 ? '' : 's'} ago`,
        href: `/documents/${doc.id}`,
        severity: 0,
      });
    } else if (days <= EXPIRY_WINDOW_DAYS) {
      items.push({
        key: `doc-expiring-${doc.id}`,
        label: doc.attributes.title ?? 'Untitled',
        detail: days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`,
        href: `/documents/${doc.id}`,
        severity: 2,
      });
    }
  }

  for (const asset of assets) {
    const at = asset.attributes;
    const href = `/portfolio/assets/asset/${asset.id}`;
    if (isMarketPriced(at.assetClass)) {
      // Priced from the market feed daily, so "no value recorded" / stale-valuation
      // below would be noise for the normal case. But a market-priced holding's value
      // is quantity x price, and `currentQuantity` comes back `undefined` (not 0) when
      // there is neither a provider-stored quantity nor a single lot (#392, #886) — see
      // `resolveQuantity` in the enricher. That is a real gap, not noise: with no lot,
      // there is no value at all, and nothing else on the Dashboard mentions it.
      //
      // `currentValue === undefined` is load-bearing here, not redundant with the
      // quantity check: `valueMarketPriced` falls back to the provider's own reported
      // valuation when quantity is unknown, so a no-lot holding that DOES have a
      // valuation already renders a value and is already counted in net worth — nudging
      // it would flag a holding that is fine. `!isSynced` matches the unvalued branch
      // below: a synced holding's quantity is the provider's business, not the family's.
      if (at.currentQuantity === undefined && at.currentValue === undefined && !isSynced(at)) {
        items.push({
          key: `asset-nolots-${asset.id}`,
          label: at.name ?? 'Untitled',
          detail: 'No holdings recorded yet',
          href,
          severity: 3,
        });
      }
      continue;
    }
    if (at.currentValue === undefined && !isSynced(at)) {
      items.push({
        key: `asset-unvalued-${asset.id}`,
        label: at.name ?? 'Untitled',
        detail: 'No value recorded yet',
        href,
        severity: 3,
      });
    } else if (isStaleValuation(at.currentValueAsOf)) {
      const days = daysSince(at.currentValueAsOf);
      items.push({
        key: `asset-stale-${asset.id}`,
        label: at.name ?? 'Untitled',
        detail: days !== null ? `Valuation is ${Math.floor(days / 30)} months old` : 'Valuation is out of date',
        href,
        severity: 1,
      });
    }
  }

  return sortAttention(items);
}

const sortAttention = (items: AttentionItem[]): AttentionItem[] =>
  items.sort((a, b) => a.severity - b.severity || compareByString<AttentionItem>(i => i.label)(a, b));

/**
 * Severity for the cash flow questions row (steward-app#2596): the same tier as the other items
 * that wait on an answer from the family (an asset with no value recorded, a stale valuation),
 * above a document that is merely expiring, so it is not always the row cut off behind "+ N more".
 */
export const CASH_FLOW_QUESTIONS_SEVERITY = 1;

/**
 * The "N cash flow questions" row, or null when there are none. `atLeast` is the façade's
 * `truncated` flag: the list was cut at its limit, so the count is a floor ("100+").
 */
export function cashFlowQuestionsItem(count: number, atLeast: boolean): AttentionItem | null {
  if (count <= 0) return null;
  const noun = count === 1 && !atLeast ? 'question' : 'questions';
  return {
    key: 'cash-flow-questions',
    label: `${count}${atLeast ? '+' : ''} cash flow ${noun}`,
    detail: 'Worth a look',
    href: '/cash-flow',
    severity: CASH_FLOW_QUESTIONS_SEVERITY,
  };
}

/** `items` with the cash flow questions row added and the list sorted as `attentionItems` sorts it. */
export function withCashFlowQuestions(items: AttentionItem[], q: { count: number; atLeast: boolean }): AttentionItem[] {
  const row = cashFlowQuestionsItem(q.count, q.atLeast);
  return row ? sortAttention([...items, row]) : items;
}
